import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/router';
import CardGrid from 'components/common/subcomponents/CardGrid';
import useMyLearning from '../../hooks/useMyLearning';
import {
  KitCard,
  KitCardBody,
  KitCardFooter,
  KitButton,
} from '@boystownorg/bi-cms-component-lib';
import hex2rgba from 'services/colorUtils/hex2rgba';
import { buttonColor } from 'services/styleUtils/buttonStyleUtils';
import { isFeatureSwitchOnClientAsync } from '../../services/featureSwitchClientUtils';
import { headingColor } from 'services/styleUtils/headingStyleUtils';

const defaultErrorMessage =
  'An unexpected error has occured. Please try again.';

const SubscriberResources = ({ module }) => {
  const { customData, fields } = module;

  const theme = useTheme();
  const router = useRouter();
  const isMyLearning = useMyLearning();
  const { data: session, status } = useSession();

  useEffect(() => {
    const setResourceFeatureSwitches = async () => {
      try {
        for (const resource of fields.resources) {
          let showOnGrid = true;
          if (resource.fields.featureSwitchName) {
            if (!session) showOnGrid = false;
            const email = session?.user?.email || '';
            if (!email) showOnGrid = false;
            try {
              const view = await isFeatureSwitchOnClientAsync(
                resource.fields.featureSwitchName,
                email,
              );
              showOnGrid = !!view;
              resource.fields.showOnGrid = showOnGrid;
            } catch (error) {
              console.error('Feature switch check failed:', error);
              resource.fields.showOnGrid = false;
            }
          } else {
            resource.fields.showOnGrid = true;
          }
        }
      } catch (error) {
        console.error('Error in resource feature switches:', error);
      }
    };

    setResourceFeatureSwitches();
  }, [session, fields?.resources]);

  const MainFrame = styled.div`
    display; flex;
    background-color: ${hex2rgba(
      headingColor(theme, fields?.backgroundColor),
      0.1,
    )};
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 25px auto 25px auto;
    width: 100%;
    @media screen and (min-width: 992px) {
      padding: 25px auto 25px auto;
    }
    @media screen and (max-width: 992px) {
      padding: 5px auto 5px auto;
    }
  `;

  const HeadingCardFrame = styled.div`
    background-color: transparent;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 0 2em;
    @media screen and (min-width: 992px) {
      margin: 40px 150px;
    }
    @media screen and (max-width: 992px) {
      margin: 10px 20px;
    }
  `;

  const Container = styled.div`
    width: 100%;
    margin-top: 30px;
    padding-bottom: 50px;
  `;

  const TitleContainer = styled.div`
    width: 100%;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    margin-left: 20px;
  `;

  const Title = styled.div`
    width: 100%;
    margin: 25px 20px 0 0;
    font-weight: 700;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    text-transform: uppercase;
    @media screen and (min-width: 992px) {
      font-size: 1.75rem;
      line-height: 2.255rem;
    }
    @media screen and (max-width: 992px) {
      font-size: 1.325rem;
      line-height: 1.575rem;
    }
  `;

  const IntroductionBox = styled.div`
    background-color: transparent;
    margin: 0;
  `;

  const IntroContainer = styled.div`
    @media screen and (min-width: 992px) {
      width: 100%;
      padding: 5px 15px 0 15px;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
    }
    @media screen and (max-width: 992px) {
      width: 100%;
      padding: 5px 5px 10px 5px;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
    }
  `;

  const CardTitle = styled.div`
    width: 100%;
    margin: 15px 0 10px 0;
    font-weight: 600;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    @media screen and (min-width: 992px) {
      font-size: 1.125rem;
      line-height: 1.313rem;
    }
    @media screen and (max-width: 992px) {
      font-size: 1.125rem;
      line-height: 1.313rem;
    }
  `;

  const CardDescription = styled.div`
    width: 100%;
    margin: 10px 0 10px 0;
    font-weight: 400;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    @media screen and (min-width: 992px) {
      font-size: 1rem;
      line-height: 1.375rem;
    }
    @media screen and (max-width: 992px) {
      font-size: 1rem;
      line-height: 1.375rem;
    }
  `;

  return (
    <MainFrame>
      {session && (
        <HeadingCardFrame>
          <TitleContainer>
            <Title style={{ color: fields.titleColor }}>{fields.title}</Title>
          </TitleContainer>
          <CardGrid style={{ width: '100%', margin: 0 }}>
            {fields.resources
              .filter((res) => res.fields.showOnGrid)
              ?.sort((a, b) => {
                if (
                  parseInt(a.fields.sortOrder, 10) >
                  parseInt(b.fields.sortOrder, 10)
                )
                  return 1;
                if (
                  parseInt(a.fields.sortOrder, 10) <
                  parseInt(b.fields.sortOrder, 10)
                )
                  return -1;
                return 0;
              })
              .map((r) => (
                <KitCard
                  key={r.contentID}
                  style={{
                    width: '21rem',
                    maxHeight: '26rem',
                  }}
                >
                  <React.Fragment key={r.contentID}>
                    <KitCardBody style={{ padding: '0 1rem' }}>
                      <CardTitle style={{ minHeight: '45px' }}>
                        {r.fields.title}
                      </CardTitle>
                      <div>
                        {r.fields.cardImage && (
                          <ImageContainer
                            style={{
                              backgroundImage: `url(${r.fields.cardImage?.url})`,
                            }}
                          ></ImageContainer>
                        )}
                      </div>
                      <CardDescription>{r.fields.description}</CardDescription>
                    </KitCardBody>
                    <KitCardFooter
                      style={{
                        width: '100%',
                        height: '60px',
                        borderTop: '1px dashed #000',
                        display: 'flex',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        padding: '.5rem 1rem',
                      }}
                    >
                      <a
                        href={r.fields?.buttonActionURL.href}
                        target={r.fields?.buttonActionURL.target}
                      >
                        <KitButton
                          size="sm"
                          round
                          style={{
                            backgroundColor: buttonColor(
                              theme,
                              r.fields?.buttonColor,
                            ),
                            color: buttonColor(theme, r.fields.buttonTextColor),
                            border:
                              '1px solid ' +
                              buttonColor(theme, r.fields.buttonBorderColor),
                          }}
                          sx={{ width: '110px' }}
                        >
                          {r.fields.buttonText}
                        </KitButton>
                      </a>
                    </KitCardFooter>
                  </React.Fragment>
                </KitCard>
              ))}
          </CardGrid>
        </HeadingCardFrame>
      )}
    </MainFrame>
  );
};

const ImageContainer = styled('div')(({ theme }) => ({
  width: '300px',
  height: '180px',
  backgroundSize: '100%',
  backgroundRepeat: 'no-repeat',
}));

const IntroText = styled('div')(({ theme }) => ({
  color: theme.palette.common.black,
  fontSize: '1.063rem',
  lineHeight: '1.75rem',
  '& li': {
    '&::before': {
      backgroundColor: '#000 !important',
    },
  },
  [theme.breakpoints.up('md')]: {
    marginBottom: '10px',
  },
  [theme.breakpoints.down('md')]: {
    marginBottom: '25px',
  },
}));

export default SubscriberResources;
